import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import MediaUpload from "@/components/MediaUpload";
import { useLang } from "@/i18n/LanguageContext";
import { translations } from "@/i18n/translations";
import { Star, Check, Loader2, Heart, ShieldCheck, PartyPopper } from "lucide-react";

const PROCEDURES = translations.en.share.procedures;
const empty = { name: "", email: "", location: "", procedure: "", clinic_name: "", quote: "", rating: 5, photo: "", video_url: "" };

export default function ShareStory() {
  const { t } = useLang();
  const [form, setForm] = useState(empty);
  const [clinics, setClinics] = useState([]);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const procLabels = t("share.procedures");
  const procLabel = (canon) => { const i = PROCEDURES.indexOf(canon); return i === -1 ? canon : procLabels[i]; };

  useEffect(() => { api.get("/clinics").then((r) => setClinics(r.data)).catch(() => {}); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const canSubmit = form.name.trim() && form.quote.trim().length >= 10;

  const submit = async () => {
    if (!canSubmit) { toast.error(t("share.errValidate")); return; }
    setLoading(true);
    try {
      await api.post("/stories", form);
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("share.errSubmit"));
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="max-w-xl mx-auto px-4 py-20 text-center" data-testid="share-success">
        <div className="w-16 h-16 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-4">
          <PartyPopper className="w-8 h-8 text-accent" />
        </div>
        <h1 className="font-heading text-3xl font-bold">{t("share.thankYou", { name: form.name.split(" ")[0] })}</h1>
        <p className="text-muted-foreground mt-3" dangerouslySetInnerHTML={{ __html: t("share.thankYouSub") }} />
        <Link to="/stories"><Button className="rounded-full mt-6 px-6" data-testid="share-view-stories-btn">{t("share.viewStories")}</Button></Link>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10">
      <div className="text-center mb-8">
        <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3"><Heart className="w-7 h-7 text-primary" /></div>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold">{t("share.title")}</h1>
        <p className="text-muted-foreground mt-2" dangerouslySetInnerHTML={{ __html: t("share.sub") }} />
      </div>

      <div className="rounded-3xl border border-border bg-white p-6 sm:p-8 space-y-5">
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>{t("share.name")}</Label>
            <Input className="h-12" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Jane D." data-testid="share-name-input" />
          </div>
          <div className="space-y-2">
            <Label>{t("share.from")}</Label>
            <Input className="h-12" value={form.location} onChange={(e) => set("location", e.target.value)} placeholder="Phoenix, AZ" data-testid="share-location-input" />
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>{t("share.procedure")}</Label>
            <Select value={form.procedure} onValueChange={(v) => set("procedure", v)}>
              <SelectTrigger className="h-12" data-testid="share-procedure-select"><SelectValue placeholder={t("share.select")} /></SelectTrigger>
              <SelectContent>{PROCEDURES.map((p) => <SelectItem key={p} value={p}>{procLabel(p)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>{t("share.clinic")}</Label>
            <Select value={form.clinic_name} onValueChange={(v) => set("clinic_name", v)}>
              <SelectTrigger className="h-12" data-testid="share-clinic-select"><SelectValue placeholder={t("share.select")} /></SelectTrigger>
              <SelectContent>{clinics.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-2">
          <Label>{t("share.emailLabel")} <span className="text-muted-foreground">{t("share.emailPrivate")}</span></Label>
          <Input className="h-12" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="jane@email.com" data-testid="share-email-input" />
        </div>

        <div className="space-y-2">
          <Label>{t("share.rating")}</Label>
          <div className="flex gap-1" data-testid="share-rating">
            {[1, 2, 3, 4, 5].map((n) => (
              <button key={n} type="button" onClick={() => set("rating", n)} data-testid={`share-star-${n}`}>
                <Star className={`w-8 h-8 transition-colors ${n <= form.rating ? "fill-amber-400 text-amber-400" : "text-muted-foreground/30"}`} />
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-2">
          <Label>{t("share.story")}</Label>
          <Textarea rows={5} value={form.quote} onChange={(e) => set("quote", e.target.value)} placeholder={t("share.storyPlaceholder")} data-testid="share-quote-input" />
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <MediaUpload label={t("share.addPhoto")} accept="image/*" kind="image" value={form.photo} onChange={(v) => set("photo", v)} testid="share-photo-upload" />
          <MediaUpload label={t("share.addVideo")} accept="video/*" kind="video" value={form.video_url} onChange={(v) => set("video_url", v)} testid="share-video-upload" />
        </div>

        <Button className="w-full rounded-full h-12" disabled={loading || !canSubmit} onClick={submit} data-testid="share-submit-btn">
          {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
          {t("share.submit")}
        </Button>
        <p className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
          <ShieldCheck className="w-3.5 h-3.5 text-accent" /> {t("share.reviewed")}
        </p>
      </div>
    </div>
  );
}
